/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
    Heading,
    FlexCol,
    FlexRow,
    TextField,
    CountryPicker,
    StatePicker,
    ZIPCodeField,
    Checkbox,
    useFlags,
    SecurityHelper,
} from 'sarsaparilla';
import NumberField from './NumberField';
import {
    changeTicketHolderDetails,
    updateTicketHolderDetails,
    toggleSaveToAccount,
} from '../../actions/orderDetailsActions';
import { VALLES_CALDERA_FACILITY_ID } from '../../constants';

export default function TicketHolderDetails({ addValidationRef, isExistingReservation }) {
    const { ticketHolderDetails } = useFlags();
    const [isSaveChangesChecked, setIsSaveChangesChecked] = useState(false);

    const dispatch = useDispatch();
    const { firstName, lastName, email, country, state, zipCode, totalNumberOfPeople } =
        useSelector(
            (reduxState) => reduxState.timedEntry.orderDetails.ticketHolderDetails
        );

    const holderDetailsDescription = useSelector(
        (reduxState) => reduxState?.timedEntry?.facility?.holder_details_description
    );

    const orderDetailsFacilityId = useSelector(
        (reduxState) => reduxState?.timedEntry?.orderDetails?.facility_id
    );

    const showTotalNumberOfPeopleField = useMemo(() => {
        return orderDetailsFacilityId === VALLES_CALDERA_FACILITY_ID;
    }, [orderDetailsFacilityId]);

    useEffect(() => {
        dispatch(updateTicketHolderDetails());
    }, [dispatch]);

    useEffect(() => {
        if (isExistingReservation || SecurityHelper.isGuest()) {
            dispatch(toggleSaveToAccount(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveChangesChange = () => {
        setIsSaveChangesChecked(!isSaveChangesChecked);
        dispatch(toggleSaveToAccount(!isSaveChangesChecked));
    };

    return (
        <section>
            <Heading headingLevel={3} appearance="h6" className="mb-2">
                Ticket Holder Details
            </Heading>
            {ticketHolderDetails && (
                <p>
                    {holderDetailsDescription ||
                        `Tickets will display the name provided below and cannot be changed
                    once the reservation is paid. Tickets are non-transferable and will
                    not be honored if altered or purchased outside of Recreation.gov.`}
                </p>
            )}
            <FlexRow className="mb-md-2">
                <FlexCol md={6} className="mb-2 mb-md-0">
                    <TextField
                        id="first-name"
                        label="First Name"
                        isRequired
                        value={firstName}
                        isDisabled={isExistingReservation}
                        onChange={(event) => {
                            dispatch(
                                changeTicketHolderDetails(
                                    {
                                        firstName: event.target.value,
                                    },
                                    true
                                )
                            );
                        }}
                        ref={addValidationRef(0)}
                        aria-label="First Name"
                        aria-required
                    />
                </FlexCol>
                <FlexCol md={6} className="mb-2 mb-md-0">
                    <TextField
                        id="last-name"
                        label="Last Name"
                        isRequired
                        isDisabled={isExistingReservation}
                        value={lastName}
                        onChange={(event) => {
                            dispatch(
                                changeTicketHolderDetails(
                                    {
                                        lastName: event.target.value,
                                    },
                                    true
                                )
                            );
                        }}
                        ref={addValidationRef(1)}
                        aria-label="Last Name"
                        aria-required
                    />
                </FlexCol>
            </FlexRow>
            <FlexRow className="mb-md-2">
                <FlexCol md={6} className="mb-2 mb-md-0">
                    <CountryPicker
                        id="country-picker"
                        isRequired
                        value={country}
                        onChange={(event) => {
                            const newCountry = event.target.value;
                            dispatch(
                                changeTicketHolderDetails(
                                    { country: newCountry, state: '' },
                                    true
                                )
                            );
                        }}
                        ref={addValidationRef(2)}
                        aria-label="Country"
                        aria-required
                    />
                </FlexCol>
                {SecurityHelper.isGuest() ? (
                    <FlexCol md={6} className="mb-2 mb-md-0">
                        <TextField
                            id="email"
                            label="Email"
                            isRequired
                            isDisabled={isExistingReservation}
                            value={email}
                            onChange={(event) => {
                                dispatch(
                                    changeTicketHolderDetails(
                                        {
                                            email: event.target.value,
                                        },
                                        true
                                    )
                                );
                            }}
                            ref={addValidationRef(6)}
                            aria-label="Email"
                            aria-required
                        />
                    </FlexCol>
                ) : null}
            </FlexRow>
            <FlexRow className="mb-md-2">
                <FlexCol md={6} className="mb-2 mb-md-0">
                    {country === 'USA' && (
                        <StatePicker
                            id="state-field"
                            isRequired
                            value={state}
                            onChange={(event) => {
                                dispatch(
                                    changeTicketHolderDetails(
                                        {
                                            state: event.target.value,
                                        },
                                        true
                                    )
                                );
                            }}
                            ref={addValidationRef(3)}
                            aria-label="State"
                            aria-required
                        />
                    )}
                    {country !== 'USA' && (
                        <TextField
                            id="state-field"
                            label="State / Province"
                            isRequired
                            isDisabled={!country}
                            value={state}
                            onChange={(event) => {
                                dispatch(
                                    changeTicketHolderDetails(
                                        {
                                            state: event.target.value,
                                        },
                                        true
                                    )
                                );
                            }}
                            ref={addValidationRef(3)}
                            aria-label="State Province"
                            aria-required
                        />
                    )}
                </FlexCol>
                <FlexCol md={6} className="mb-2 mb-md-0">
                    {country === 'USA' && (
                        <ZIPCodeField
                            id="zip-code-field"
                            isRequired
                            value={zipCode}
                            onChange={(event) => {
                                dispatch(
                                    changeTicketHolderDetails(
                                        {
                                            zipCode: event.target.value,
                                        },
                                        true
                                    )
                                );
                            }}
                            ref={addValidationRef(4)}
                            aria-label="Zip code"
                            aria-required
                        />
                    )}
                    {country !== 'USA' && (
                        <TextField
                            id="zip-code-field"
                            label="ZIP / Postal Code"
                            isRequired
                            value={zipCode}
                            onChange={(event) => {
                                dispatch(
                                    changeTicketHolderDetails(
                                        {
                                            zipCode: event.target.value,
                                        },
                                        true
                                    )
                                );
                            }}
                            ref={addValidationRef(4)}
                            aria-label="ZIP / Postal Code"
                            aria-required
                        />
                    )}
                </FlexCol>
            </FlexRow>
            {showTotalNumberOfPeopleField && (
                <FlexRow className="mb-md-2">
                    <FlexCol md={6} className="mb-2 mb-md-0">
                        <NumberField
                            id="total-passenger-count-field"
                            aria-label="Number of people in the vehicle"
                            aria-required
                            visualLabel="Number of people in the vehicle"
                            min={1}
                            max={8}
                            units="people"
                            isRequired
                            value={totalNumberOfPeople}
                            onChange={(value) => {
                                dispatch(
                                    changeTicketHolderDetails(
                                        { totalNumberOfPeople: value },
                                        true
                                    )
                                );
                            }}
                            ref={addValidationRef(5)}
                        />
                    </FlexCol>
                </FlexRow>
            )}
            {!SecurityHelper.isGuest() ? (
                <Checkbox
                    id="save-changes"
                    label="Save above changes to my account"
                    isChecked={isSaveChangesChecked}
                    onChange={handleSaveChangesChange}
                />
            ) : null}
        </section>
    );
}

TicketHolderDetails.propTypes = {
    addValidationRef: PropTypes.func,
    isExistingReservation: PropTypes.bool,
};
